<template>
  <div>
    <div class="fields-block">
      <h3 class="mb-4" v-if="loggedIn">{{ $t('payment.customer.welcome_back') }} <span>{{ first_name }}</span></h3>
      <h2>{{ $t('payment.customer.confirmation_to') }}</h2>

      <div class="row">
        <div class="col-md-6 field-col">
          <div class="fields-area">
            <input-base
              v-model="email"
              id="email" 
              type="email" 
              label="payment.customer.email"
              :invalid="value.customer.email.$error"
              @change="value.customer.email.$touch()"
              @input="checkEmail"
              @focusout="mxm"
              :disabled="loggedIn"
            />
            <span class="error">{{ validationMsg(value.customer.email) }}</span>
          </div>
        </div>
        <div class="col-md-6 field-col" v-if="loggedIn"> 
          <div class="fields-area logout-button">
            <button class="btn btn-primary" @click="logout">{{ $t('payment.customer.logout') }}</button>
          </div>
        </div>
        <transition name="fade-medium">
          <div class="col-md-6 field-col" v-if="guestLogin && existingUser && !loggedIn">
            <div class="fields-area">
              <input-base
                v-model="userPassword"
                id="password" 
                type="password" 
                label="payment.customer.password"
              />
              <span v-if="incorrectLogins" class="error">{{ $t('payment.customer.incorrect_email_password') }}</span>
            </div>
            <div class="row existing-user">
              <div class="col-12 col-md-4">
                <button class="btn btn-primary" :disabled="userPassword.length < 1" @click="login">
                  {{ $t('payment.customer.log_in') }}
                </button>
              </div>
              <div class="col-12 col-md-8">
                <a nohref class="pb-1" @click="guestLogin = false">{{ $t('payment.customer.continue_password') }}</a>
                <a nohref class="mt-1" @click="password = true">{{ $t('payment.customer.forgotten_password') }}</a>
              </div>
            </div>
          </div>
        </transition>
      </div>
      <span class="text mt-1">
        {{ $t('payment.customer.confirmation_to_email') }}
      </span>
    </div>
    <div class="fields-block">
      <h2>{{ $t('payment.customer.customer_information') }}</h2>
      <div class="row">
        <div class="col-md-6 field-col">
          <div class="fields-area style01">
            <input-select
              v-model="titleFormatted"
              id="title"
              :inline="true"
              :options="titles"
              label="payment.customer.title"
            />
            <div>
              <input-base
                v-model="first_name"
                id="first_name" 
                type="text" 
                label="payment.customer.first_name"
                :invalid="value.customer.first_name.$error"
                @change="value.customer.first_name.$touch()"
              />
              <span class="error">{{ validationMsg(value.customer.first_name) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 field-col">
          <input-base
            v-model="last_name"
            id="last_name" 
            type="text" 
            label="payment.customer.last_name" 
            :invalid="value.customer.last_name.$error"
             @change="value.customer.last_name.$touch()"
          />
          <span class="error">{{ validationMsg(value.customer.last_name) }}</span>
        </div>
        <div class="col-md-6 field-col">
          <input-base
            v-model="mobile"
            id="mobile" 
            type="text" 
            label="payment.customer.mobile"
            :invalid="value.customer.mobile.$error"
            @change="value.customer.mobile.$touch()"
          />
          <span class="error">{{ validationMsg(value.customer.mobile) }}</span>
        </div>
      </div>
      <div v-if="category === 'airportlounge'">
        <div class="row" v-for="(passenger, key) in passengers" :key="key">
          <h5 class="col-12 field-col mb-2 lounge-passenger">
            {{ $t(`payment.customer.${passenger.type}`)}}
            {{ $t('payment.customer.no') }}.
            {{ passenger.index + 1}}
          </h5>
          <div class="col-md-6 field-col">
            <input-base
              v-model="passenger.first_name"
              :id="`first_name-${key}`" 
              type="text" 
              label="payment.customer.first_name"
              :invalid="value.customer.passengers.$each[key].first_name.$error"
              @input="save(key)"
              @change="value.customer.passengers.$each[key].first_name.$touch()"
            />
            <span class="error">{{ validationMsg(value.customer.passengers.$each[key].first_name) }}</span>
          </div>
          <div class="col-md-6 field-col">
            <input-base
              v-model="passenger.last_name"
              :id="`last_name-${key}`" 
              type="text" 
              label="payment.customer.last_name"
              :invalid="value.customer.passengers.$each[key].last_name.$error"
              @input="save(key)"
              @change="value.customer.passengers.$each[key].last_name.$touch()"
            />
            <span class="error">{{ validationMsg(value.customer.passengers.$each[key].last_name) }}</span>
          </div>
          <hr class="col-12 p-0 mb-4 mt-0">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sync, call, get } from "vuex-pathify";
import * as _ from "lodash";
import { InputBase, InputSelect } from "@/components";
import mixins from "@/mixins";
import times from "@/utils/timeOptions";

export default {
  name: "customer",
  mixins: [mixins.validate, mixins.load],
  components: { InputBase, InputSelect },
  props: ['value'],
  computed: {
    ...sync("customer/*"),
    password: sync("modals/passwordReset"),
    category: get("quote/category"),
    passengers: sync("customer/passengers"),
    loggedIn: sync("auth/loggedIn"),
    customer() { 
      return this.$store.state.customer;
    },
    total: get("cart/total"),
    titleFormatted() {
      return this.title.toUpperCase()
    }
  },
  data() {
    return {
      timeOptions: times,
      titles: [
        { label: this.$t('payment.customer.titles.mr'), value: this.$t('payment.customer.titles.mr').toUpperCase() },
        { label: this.$t('payment.customer.titles.ms'), value: this.$t('payment.customer.titles.ms').toUpperCase() },
        { label: this.$t('payment.customer.titles.mrs'), value: this.$t('payment.customer.titles.mrs').toUpperCase() },
        { label: this.$t('payment.customer.titles.miss'), value: this.$t('payment.customer.titles.miss').toUpperCase() },
        { label: this.$t('payment.customer.titles.dr'), value: this.$t('payment.customer.titles.dr').toUpperCase() }
      ],
      userPassword: '',
      incorrectLogins: false,
      guestLogin: true,
    }
  },
  created() {
    this.$loadScript("https://maxemail.emailcenteruk.com/scripts/basket.js")
  },
  methods: {
    emailLookup: call("customer/emailLookup"),
    customerLogin: call("customer/customerLogin"),
    updatePassenger: call("customer/updatePassenger"),
    mxmSet: call("customer/mxmPayment"),
    clearAuth: call("customer/logout"),
    save(index) {
      this.updatePassenger({ index, passenger: this.passengers[index] })
    },
    login() {
      this.incorrectLogins = false;

      if(this.userPassword.length > 0) {

        this.customerLogin({ email: this.email, password: this.userPassword }).then(() => {

          // if correct logins 
          if(!this.loggedIn) return this.incorrectLogins = true;
          window.location.reload()
        })
      }
    },
    logout() {
      this.clearAuth().then(() => {
        window.location.reload();
      })
    },
    checkEmail: _.debounce( function() { 
      this.userPassword = '';
      this.guestLogin = true;
      !this.value.customer.email.$invalid ? this.emailLookup(this.email) : this.existingUser = false;
    }, 500),
    mxm() {
      if(!this.value.customer.email.$invalid) this.mxmSet(this.email)
    }
  }
};
</script>